

/**
 * @param {string} path  path to the element to be changed
 *  @example  "person.address.city"
 *  @example  "students[id=3].gpa"
 * @param {any} value  new value to put in 
 * @param {Object} obj  the object being modified
 */

export function __update(path:string, updates:any, obj:any) {
  const keys = Array.isArray(path) ? path : path.split('.');

  // Create a copy of the object to ensure immutability
  const newObj = { ...obj };

  let currentLevel = newObj;

  // Traverse through the keys and stop at the last key
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];

    // Check for array condition, e.g., "students.parttime[id=4]"
    const arrayMatch = key.match(/(.+)\[(.+)\]/);

    if (arrayMatch) {
      const arrayKey = arrayMatch[1];
      const [property, propValue] = arrayMatch[2].split('=');

      // Ensure propValue is correctly typed (e.g., as a number)
      const targetValue = isNaN(propValue) ? propValue : Number(propValue);

      // Ensure the array is copied to avoid mutation
      currentLevel[arrayKey] = [...currentLevel[arrayKey]];

      // Find the object in the array matching the condition
      const index = currentLevel[arrayKey].findIndex((item:any) => item[property] === targetValue);

      if (index !== -1) {
        // Check if this is the last key in the path
        if (i === keys.length - 1) {
          // If it's the last key, apply updates directly to this object
          currentLevel[arrayKey][index] = {
            ...currentLevel[arrayKey][index],
            ...updates,
          };
          return newObj; // Return early since the update is done
        } else {
          // Copy the found item to avoid mutation and set it as the new current level
          currentLevel[arrayKey][index] = { ...currentLevel[arrayKey][index] };
          currentLevel = currentLevel[arrayKey][index];
        }
      } else {
        throw new Error(
          `Item with ${property}=${targetValue} not found in array "${arrayKey}".`
        );
      }
    } else {
      // If it's a regular key, copy the object to avoid mutation
      if (i === keys.length - 1) {
        // Apply the update directly if it's the last key
        currentLevel[key] = updates;
      } else {
        currentLevel[key] = { ...currentLevel[key] };
        currentLevel = currentLevel[key];
      }
    }
  }

  return newObj;
}




// Update multiple keys
export function __updateMany(updates:any, obj:any) {
  return updates.reduce((acc:any, [path, value]:any) => {
    return __update(path, value, acc);
  }, obj);
}



// Insert Function for Arrays
export function __insert(path:string, newItem:any, obj:any) {
  const keys:any[] = Array.isArray(path) ? path : path.split('.');
  
  // Access the current array (or default to an empty array)
  const array = keys.reduce((acc, key) => acc[key], obj) || [];

  // Insert the new item into the array
  const newArray = [...array, newItem];

  // Use the update function to apply the new array to the original object
  return __update(path, newArray, obj);
}



// Remove Function for Arrays
export function __remove(path:string, itemToRemove:any, obj:any) {
  const keys:any[] = Array.isArray(path) ? path : path.split('.');

  // Access the current array
  const array = keys.reduce((acc, key) => acc[key], obj) || [];

  // Remove the item from the array based on a condition (e.g., matching `id`)
  const newArray = array.filter( (item:any) => item.id !== itemToRemove.id);

  // Use the update function to apply the new array to the original object
  return __update(path, newArray, obj);
}

export const immutables = {
  __update,
  __updateMany,
  __insert,
  __remove
}